import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AccountData, EnrichedEventData } from '../../../api';
import { AreasPinSource, HIGHLIGHT_REQUEST_VALIDITY_MS, useAreasView } from '../AreasViewContext';
import { AgendaCardState, AgendaSlotGroup } from '../utils/occupancyMath';
import { OrderPlacement } from '../utils/orderAreas';
import { FONT_SIZE, HIDDEN_SCROLLBAR_SX, PHONE_FAB_CLEARANCE } from '../agendaStyles';
import { AgendaNowDivider } from './AgendaNowDivider';
import { AgendaOrderCard } from './AgendaOrderCard';
import { AgendaSlotHeader } from './AgendaSlotHeader';
import { SessionCard } from './SessionCard';

type AgendaListProps = {
  groups: AgendaSlotGroup[];
  states: Record<string, AgendaCardState>;
  placements: Record<string, OrderPlacement>;
  sessionTitles: Record<string, string>;
  onOpenSession: (event: EnrichedEventData) => void;
  nextUpId: string | null;
  newIds: Set<string>;
  lateById: Record<string, number | null>;
  account: AccountData;
  language: 'he' | 'en';
  timeFormat: string;
  isToday: boolean;
  nowSeconds: number;
  /** No orders at all vs. nothing matches the search or filter. */
  emptyMessage: string;
  isDragEnabled: boolean;
  footer?: React.ReactNode;
};

/** A scroll request older than this is stale (the day's orders never arrived, or the user moved on). */
const SCROLL_REQUEST_VALIDITY_MS = 5000;
/** How long after an explicit pick, or a programmatic scroll, scrolling must not move the pinned time. */
const EXPLICIT_PICK_GRACE_MS = 1000;
/** How long the new-order frame stays before it clears on its own. */
const HIGHLIGHT_DURATION_MS = 8000;
/** Phone: how far the list must be scrolled down before the top folds away, and how far back up to bring it back. */
const PHONE_COLLAPSE_THRESHOLD_PX = 24;
const PHONE_EXPAND_DISTANCE_PX = 24;
/** Ignores sub-pixel / bounce noise when deciding the scroll direction. */
const PHONE_DIRECTION_NOISE_PX = 2;
const SLOT_ATTRIBUTE = 'data-slot-seconds';
const NOW_DIVIDER_ATTRIBUTE = 'data-now-divider';

/**
 * The chronological list of order cards, grouped by start slot, wired to the pinned time both ways.
 *
 * Scrolling pins the strip to the slot at the top of the list, but only scrolling the user did: a
 * programmatic scroll (after "now", the time picker or a strip cell) must not overwrite the time
 * they just chose, even when the list lands on a later group because the chosen slot has no orders.
 */
export const AgendaList = ({
  groups,
  states,
  placements,
  sessionTitles,
  onOpenSession,
  nextUpId,
  newIds,
  lateById,
  account,
  language,
  timeFormat,
  isToday,
  nowSeconds,
  emptyMessage,
  isDragEnabled,
  footer,
}: AgendaListProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {
    lastExplicitAt,
    scrollRequest,
    pinTime,
    enterLiveMode,
    selectedSeconds,
    searchQuery,
    highlightRequest,
    clearHighlight,
    setIsPhoneTopCollapsed,
  } = useAreasView();
  const isSearching = searchQuery.trim().length > 0;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const handledTokenRef = useRef<number | null>(null);
  const handledHighlightTokenRef = useRef<number | null>(null);
  const highlightTimeoutRef = useRef<number | undefined>(undefined);
  /** Scroll events before this are ours (a scrollIntoView in flight), not the user's. */
  const ignoreScrollUntilRef = useRef(0);
  /** Set by a scroll event outside the ignore window; cleared by every explicit pick. */
  const userScrolledRef = useRef(false);
  const lastExplicitSeenRef = useRef(lastExplicitAt);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  /** The list's own height: the spacer below the last group is this tall, so any group can reach the top. */
  const [spacerHeight, setSpacerHeight] = useState<number | null>(null);

  // The observer outlives renders; it reads the latest values through this ref.
  const latestRef = useRef({
    lastExplicitAt,
    pinTime,
    enterLiveMode,
    selectedSeconds,
    isToday,
    scrollRequest,
    isSearching,
  });
  latestRef.current = { lastExplicitAt, pinTime, enterLiveMode, selectedSeconds, isToday, scrollRequest, isSearching };
  /** Where the list stood when a search began, so clearing it lands back there. */
  const savedScrollTopRef = useRef<number | null>(null);
  const wasSearchingRef = useRef(false);

  // A new explicit pick starts a fresh "has the user scrolled since" window.
  if (lastExplicitAt !== lastExplicitSeenRef.current) {
    lastExplicitSeenRef.current = lastExplicitAt;
    userScrolledRef.current = false;
    ignoreScrollUntilRef.current = Math.max(ignoreScrollUntilRef.current, lastExplicitAt + EXPLICIT_PICK_GRACE_MS);
  }

  const isExpandedCard = (orderId: string) => (isSmallScreen ? expandedId === orderId : expandedIds.has(orderId));

  const isHighlightedItem = (itemId: string) =>
    highlightRequest?.itemId === itemId && performance.now() - highlightRequest.at <= HIGHLIGHT_REQUEST_VALIDITY_MS;

  const toggleCard = (orderId: string) => {
    if (highlightRequest?.itemId === orderId) {
      clearHighlight();
    }
    if (isSmallScreen) {
      setExpandedId((current) => (current === orderId ? null : orderId));
      return;
    }
    setExpandedIds((current) => {
      const next = new Set(current);
      if (!next.delete(orderId)) {
        next.add(orderId);
      }
      return next;
    });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const intersecting = new Map<Element, boolean>();

    const evaluate = () => {
      if (!userScrolledRef.current) {
        return;
      }
      const latest = latestRef.current;
      // A search narrows the list to a few cards; the strip keeps answering for the time the user had.
      if (latest.isSearching) {
        return;
      }
      // A scroll request that has not landed yet is about to move the list; pinning from the stale
      // offset in between would briefly contradict the time that request was made for.
      const pendingRequest =
        latest.scrollRequest &&
        handledTokenRef.current !== latest.scrollRequest.token &&
        performance.now() - latest.scrollRequest.at <= SCROLL_REQUEST_VALIDITY_MS;
      if (pendingRequest) {
        return;
      }
      const visible = Array.from(intersecting.entries())
        .filter(([, isVisible]) => isVisible)
        .map(([element]) => element);
      if (latest.isToday && visible.some((element) => element.hasAttribute(NOW_DIVIDER_ATTRIBUTE))) {
        latest.enterLiveMode(AreasPinSource.SCROLL);
        return;
      }
      const topSeconds = visible
        .filter((element) => element.hasAttribute(SLOT_ATTRIBUTE))
        .map((element) => Number(element.getAttribute(SLOT_ATTRIBUTE)))
        .sort((a, b) => a - b)[0];
      if (topSeconds !== undefined && topSeconds !== latest.selectedSeconds) {
        latest.pinTime(topSeconds, AreasPinSource.SCROLL);
      }
    };

    const onScroll = () => {
      if (performance.now() < ignoreScrollUntilRef.current) {
        return;
      }
      userScrolledRef.current = true;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => intersecting.set(entry.target, entry.isIntersecting));
        evaluate();
      },
      { root: container, threshold: 0, rootMargin: '0px 0px -85% 0px' },
    );
    container
      .querySelectorAll(`[${SLOT_ATTRIBUTE}], [${NOW_DIVIDER_ATTRIBUTE}]`)
      .forEach((element) => observer.observe(element));
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', onScroll);
    };
  }, [groups]);

  // Phone: scrolling the list down folds the stats, toolbar and strip away; scrolling back up (or reaching the top)
  // brings them back. Scroll events inside the existing programmatic-scroll window are not the user's and never toggle.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !isSmallScreen) {
      return undefined;
    }
    let lastTop = container.scrollTop;
    let travelled = 0;
    let frame = 0;
    let settleTimeout: number | undefined;

    const settle = () => {
      lastTop = container.scrollTop;
      travelled = 0;
      if (lastTop <= 0) {
        setIsPhoneTopCollapsed(false);
      }
    };

    const evaluate = () => {
      frame = 0;
      const top = container.scrollTop;
      const remainingIgnoreMs = ignoreScrollUntilRef.current - performance.now();
      if (remainingIgnoreMs > 0) {
        lastTop = top;
        travelled = 0;
        window.clearTimeout(settleTimeout);
        settleTimeout = window.setTimeout(settle, remainingIgnoreMs + 50);
        return;
      }
      const delta = top - lastTop;
      lastTop = top;
      if (top <= 0) {
        travelled = 0;
        setIsPhoneTopCollapsed(false);
        return;
      }
      if (Math.abs(delta) < PHONE_DIRECTION_NOISE_PX) {
        return;
      }
      travelled = Math.sign(delta) === Math.sign(travelled) ? travelled + delta : delta;
      if (travelled > 0 && top > PHONE_COLLAPSE_THRESHOLD_PX) {
        setIsPhoneTopCollapsed(true);
      } else if (travelled <= -PHONE_EXPAND_DISTANCE_PX) {
        setIsPhoneTopCollapsed(false);
      }
    };

    const onPhoneScroll = () => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(evaluate);
      }
    };

    container.addEventListener('scroll', onPhoneScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onPhoneScroll);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimeout);
      setIsPhoneTopCollapsed(false);
    };
  }, [isSmallScreen, setIsPhoneTopCollapsed]);

  // Layout effect: the full list is back in the DOM in the same commit the search clears, so the
  // saved offset can be applied before the browser paints the top of the list.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    if (isSearching && !wasSearchingRef.current) {
      savedScrollTopRef.current = container.scrollTop;
    } else if (!isSearching && wasSearchingRef.current && savedScrollTopRef.current !== null) {
      ignoreScrollUntilRef.current = performance.now() + EXPLICIT_PICK_GRACE_MS;
      container.scrollTop = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
    }
    wasSearchingRef.current = isSearching;
  }, [isSearching]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(() => setSpacerHeight(container.clientHeight));
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!scrollRequest || !container) {
      return;
    }
    if (
      handledTokenRef.current === scrollRequest.token ||
      performance.now() - scrollRequest.at > SCROLL_REQUEST_VALIDITY_MS
    ) {
      return;
    }
    const wrappers = Array.from(container.querySelectorAll<HTMLElement>(`[${SLOT_ATTRIBUTE}]`));
    const nowDivider = container.querySelector<HTMLElement>(`[${NOW_DIVIDER_ATTRIBUTE}]`);
    const target =
      isToday && scrollRequest.toNow && nowDivider
        ? nowDivider
        : wrappers.find((wrapper) => Number(wrapper.getAttribute(SLOT_ATTRIBUTE)) >= scrollRequest.seconds) ??
          wrappers[wrappers.length - 1];
    if (!target) {
      return;
    }
    // The scroll we are about to cause is not the user's; the time they picked must survive it.
    ignoreScrollUntilRef.current = performance.now() + EXPLICIT_PICK_GRACE_MS;
    userScrolledRef.current = false;
    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    handledTokenRef.current = scrollRequest.token;
  }, [scrollRequest?.token, groups]);

  // The new order's card may not be in the list yet (its day or cache is still loading); this re-runs
  // when the groups change, until the card shows up.
  useEffect(() => {
    const container = scrollRef.current;
    if (
      !highlightRequest ||
      !container ||
      handledHighlightTokenRef.current === highlightRequest.token ||
      performance.now() - highlightRequest.at > HIGHLIGHT_REQUEST_VALIDITY_MS
    ) {
      return;
    }
    const card = container.querySelector<HTMLElement>(`[data-agenda-item-id="${CSS.escape(highlightRequest.itemId)}"]`);
    if (!card) {
      return;
    }
    ignoreScrollUntilRef.current = performance.now() + EXPLICIT_PICK_GRACE_MS;
    userScrolledRef.current = false;
    card.scrollIntoView({ block: 'center', behavior: 'smooth' });
    handledHighlightTokenRef.current = highlightRequest.token;
    window.clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = window.setTimeout(clearHighlight, HIGHLIGHT_DURATION_MS);
  }, [highlightRequest?.token, groups]);

  useEffect(() => () => window.clearTimeout(highlightTimeoutRef.current), [highlightRequest?.token]);
  useEffect(() => clearHighlight, [clearHighlight]);

  const firstLaterGroupIndex = groups.findIndex((group) => group.startSeconds > nowSeconds);
  const nowDividerIndex =
    !isToday || groups.length === 0 ? -1 : firstLaterGroupIndex === -1 ? groups.length : firstLaterGroupIndex;
  const nowDivider = <AgendaNowDivider key="now-divider" nowSeconds={nowSeconds} timeFormat={timeFormat} />;

  return (
    <Grid
      ref={scrollRef}
      sx={{
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        // Desktop: a 4px inset (cancelled by the negative margin) keeps the highlight outline from being clipped.
        paddingInline: isSmallScreen ? 1.5 : 0.5,
        // Phone: the floating "+" sits at the bottom corner; the last card scrolls clear of it.
        ...(isSmallScreen && { paddingBottom: PHONE_FAB_CLEARANCE }),
        ...(!isSmallScreen && { marginInline: -0.5 }),
        ...HIDDEN_SCROLLBAR_SX,
      }}>
      {groups.length === 0 && (
        <Typography sx={{ fontSize: FONT_SIZE.secondary, color: 'text.secondary', padding: '16px 4px' }}>
          {emptyMessage}
        </Typography>
      )}
      {groups.map((group, index) => (
        <React.Fragment key={group.slot}>
          {index === nowDividerIndex && nowDivider}
          <Grid {...{ [SLOT_ATTRIBUTE]: group.startSeconds }} sx={{ marginBottom: 1.75 }}>
            <AgendaSlotHeader
              startSeconds={group.startSeconds}
              sessionCount={group.sessions.length}
              orderCount={group.orders.length}
              persons={group.persons}
              timeFormat={timeFormat}
            />
            <Grid sx={{ paddingTop: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {group.sessions.map((session) => (
                <SessionCard
                  key={session.event.id}
                  session={session}
                  title={sessionTitles[session.event.id]}
                  account={account}
                  timeFormat={timeFormat}
                  isHighlighted={isHighlightedItem(session.event.id)}
                  onOpen={() => {
                    if (highlightRequest?.itemId === session.event.id) {
                      clearHighlight();
                    }
                    onOpenSession(session.event);
                  }}
                />
              ))}
              {group.orders.map((order) => (
                <AgendaOrderCard
                  key={order.id}
                  order={order}
                  state={states[order.id]}
                  placement={placements[order.id]}
                  account={account}
                  language={language}
                  timeFormat={timeFormat}
                  isToday={isToday}
                  nowSeconds={nowSeconds}
                  isNextUp={nextUpId === order.id}
                  lateMinutes={lateById[order.id] ?? null}
                  isNew={newIds.has(order.id)}
                  isHighlighted={isHighlightedItem(order.id)}
                  isExpanded={isExpandedCard(order.id)}
                  isDragEnabled={isDragEnabled}
                  onToggleExpanded={() => toggleCard(order.id)}
                />
              ))}
            </Grid>
          </Grid>
        </React.Fragment>
      ))}
      {nowDividerIndex === groups.length && nowDivider}
      {footer}
      {/* Room below the last group so the last slot, and the now divider late in the day, can reach the
          top of the list - otherwise neither could ever become the viewed slot. */}
      <Grid sx={{ height: spacerHeight ?? '60vh', flexShrink: 0 }} />
    </Grid>
  );
};
